import { type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip as AlchemyTooltip } from '@m/alchemy-ui/Tooltip';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { useImageUrl } from '../../../hooks';
import { isUserAccessRestricted } from '../../../helpers/userAccess';
import { UserType } from '../../../common/enums/UserType';
import { ANONYMOUS_USER_ID } from '../../shared-constants';
import { UserAvatar } from '../../community-page/components/UserAvatar/UserAvatar';
import { type IdeaUserData } from '../IdeaCard.types';
import { openUserProfilePopup, USER_TYPE_LABELS } from '../IdeaCard.helpers';
import * as Styled from './IdeaCard.styled';

const AVATAR_SIZE = 60;

interface IdeaAuthorProps {
  readonly ideaId: string;
  readonly user: IdeaUserData;
  readonly isAnonymous: boolean;
  readonly createdAt: string;
  readonly hasTopBadge?: boolean;
}

export const IdeaAuthor = ({ ideaId, user, isAnonymous, createdAt, hasTopBadge }: IdeaAuthorProps) => {
  const intl = useIntl();
  const authorId = user?.id;
  const authorName = isAnonymous
    ? intl.formatMessage({ id: 'anonymous', defaultMessage: 'Anonymous' })
    : (user?.displayName ?? '');
  const profileImageFile = !isAnonymous && user?.profileImageFile ? user.profileImageFile : null;
  const userType = Number(user?.userAccess?.userType);
  const isAccessRestricted = isUserAccessRestricted(userType as UserType);
  const roleLabel = isAccessRestricted ? undefined : USER_TYPE_LABELS[userType];
  const roleLabelText = roleLabel ? `(${roleLabel})` : undefined;
  const isInactive = user?.userAccess?.userType === UserType.NO_ACCESS;

  const avatarUrl = useImageUrl(profileImageFile, AVATAR_SIZE, AVATAR_SIZE);

  const handleProfileClick = (e: MouseEvent<HTMLButtonElement | HTMLElement>) => {
    e.preventDefault();
    if (authorId) {
      openUserProfilePopup(authorId, intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' }), e.currentTarget);
    }
  };

  const canOpenProfile = !isAnonymous && authorId && String(authorId) !== String(ANONYMOUS_USER_ID);

  return (
    <Styled.AuthorSection
      $hasTopBadge={hasTopBadge}
      role="region"
      aria-label={`${intl.formatMessage({ id: 'authorInformation', defaultMessage: 'Author information for idea' })} ${ideaId} : ${authorName}`}
    >
      <Styled.AvatarFigure>
        <UserAvatar
          user={{
            id: authorId ?? '',
            name: authorName,
            avatarUrl,
            linkUrl: canOpenProfile ? `/user/view/${authorId}` : '',
            userAccess: user?.userAccess ? { userType: user.userAccess.userType } : undefined
          }}
          onClick={canOpenProfile ? handleProfileClick : undefined}
        />
      </Styled.AvatarFigure>

      <Styled.AuthorInfo>
        <AlchemyTooltip
          trigger={
            <Styled.AuthorName>
              <span>
                <FormattedMessage id="by" defaultMessage="by" />
              </span>{' '}
              {isAnonymous ? (
                <span>{authorName}</span>
              ) : (
                <Styled.AuthorButton
                  type="button"
                  style={{ WebkitBoxOrient: 'vertical' }}
                  aria-label={`${intl.formatMessage({ id: 'openProfileDialog', defaultMessage: 'Open profile dialog for' })} ${authorName}`}
                  onClick={handleProfileClick}
                >
                  {authorName}
                </Styled.AuthorButton>
              )}
            </Styled.AuthorName>
          }
          placement="top"
          contents={authorName}
        />
        <AlchemyTooltip
          trigger={
            <Styled.RelativeTime>
              {roleLabelText && <span>{roleLabelText} </span>}
              {isInactive && (
                <span>
                  <FormattedMessage id="inactive" defaultMessage="(Inactive)" />{' '}
                </span>
              )}
              {formatDistanceToNowStrict(new Date(createdAt), { addSuffix: true })}
            </Styled.RelativeTime>
          }
          placement="bottom"
          contents={format(new Date(createdAt), 'h:mm a - d MMM yyyy')}
        />
      </Styled.AuthorInfo>
    </Styled.AuthorSection>
  );
};
